import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AppLayout, LoadingSpinner } from '../components/Layout';
import {
  Briefcase, MapPin, Database, BarChart3, CheckCircle2, Lock, Flag, Circle, Loader2,
  FileText, ChevronDown, ArrowRight, Users, User, Target, RefreshCw, Info,
} from 'lucide-react';
import axios from 'axios';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => (n || n === 0 ? `$${Math.round(n).toLocaleString()}` : '-');
const moneyK = (n) => (n >= 1000000 ? `$${(n / 1000000).toFixed(1)}M` : `$${Math.round(n / 1000)}K`);

const C = {
  bg: '#021735', surface: '#09254D', surface2: '#0D2C55', elevated: '#12335D', border: '#25568F',
  cyan: '#29E6F4', blue: '#339AE5', electric: '#506FEF', purple: '#8358F5',
  teal: '#2DDBA5', gold: '#F2C94C', pink: '#F05A87',
  text: '#F7FAFF', text2: '#C4D2E3', muted: '#8EA6C1',
};

const STATUS = {
  in_progress: { label: 'In Progress', hex: C.gold },
  not_started: { label: 'Not Started', hex: C.muted },
  upcoming: { label: 'Upcoming', hex: C.blue },
  completed: { label: 'Completed', hex: C.teal },
  ready: { label: 'Ready', hex: C.cyan },
  at_risk: { label: 'At Risk', hex: C.pink },
};
const SOURCE_ROUTE = {
  'Skill Gap': '/app/skills', 'Career Plan': '/app/career-intelligence',
  'Opportunities': '/app/internships', 'Personal Brand': '/app/proof-builder',
  'Academic Requirements': '/app/career-intelligence',
};
const TABS = ['Timeline', 'Courses', 'Experience', 'Skills', 'Network', 'Brand'];
const TAB_SOURCE = { Courses: ['Academic Requirements', 'Career Plan'], Experience: ['Opportunities'], Skills: ['Skill Gap'], Network: ['Network'], Brand: ['Personal Brand'] };
// Reusable glowing card style so every box has the premium glow.
const box = (hex = C.cyan, on = false) => ({
  background: on ? C.elevated : C.surface,
  border: `1px solid ${on ? hex : C.border}`,
  boxShadow: on ? `0 0 26px ${hex}66, inset 0 0 20px ${hex}14` : `0 0 22px ${hex}22`,
});

function StageNode({ stage, idx, total }) {
  const cur = stage.state === 'current', done = stage.state === 'done';
  const isLaunch = stage.key === 'launch';
  const hex = cur ? C.cyan : done ? C.teal : isLaunch ? C.purple : C.blue;
  const Icon = done ? CheckCircle2 : cur ? Loader2 : isLaunch ? Flag : stage.key === 'senior' ? Lock : Circle;
  return (
    <div className="relative flex-1 min-w-[150px]">
      {idx < total - 1 && <div className="hidden md:block absolute top-6 left-1/2 w-full h-0.5" style={{ background: `linear-gradient(90deg, ${hex}, ${C.border})` }} />}
      <div className="relative rounded-2xl p-4 border text-center" data-testid={`stage-${stage.key}`}
        style={{ background: cur ? C.elevated : C.surface, borderColor: cur ? C.cyan : hex, boxShadow: cur ? `0 0 24px ${C.cyan}66` : `0 0 16px ${hex}33` }}>
        <div className="w-12 h-12 mx-auto rounded-full flex items-center justify-center" style={{ background: `${hex}1f`, border: `2px solid ${hex}` }}>
          <Icon className={`w-6 h-6 ${cur ? 'animate-spin-slow' : ''}`} style={{ color: hex }} />
        </div>
        <p className="mt-2 text-sm font-bold" style={{ color: C.text }}>{stage.label}</p>
        <p className="text-[11px] font-semibold tracking-wide" style={{ color: hex }}>{stage.phase}</p>
        <p className="text-[11px] mt-1" style={{ color: C.muted }}>{stage.years}</p>
      </div>
    </div>
  );
}

export default function MyStrategyPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [raw, setRaw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState('Timeline');
  const [priorityOpen, setPriorityOpen] = useState(true);
  const [done, setDone] = useState({});

  const load = useCallback(async () => {
    if (!token) return;
    const r = await axios.get(`${API}/college/my-strategy`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null);
    if (r) setRaw(r.data);
  }, [token]);

  useEffect(() => { load().finally(() => setLoading(false)); }, [load]);

  const refresh = async () => { setRefreshing(true); await load(); setRefreshing(false); };

  if (loading) return <AppLayout><div className="flex items-center justify-center min-h-[60vh]"><LoadingSpinner size="lg" /></div></AppLayout>;
  const v = raw?.strategy_v2;
  if (!v) return <AppLayout><div className="text-center py-20" style={{ color: C.text2 }}>Complete Design My Life and set a career target to build your strategy.</div></AppLayout>;

  const h = v.header;
  const milestones = v.milestones || [];
  const shown = tab === 'Timeline' ? milestones : milestones.filter((m) => (TAB_SOURCE[tab] || []).includes(m.source));

  return (
    <AppLayout>
      <style>{`@keyframes spin-slow{to{transform:rotate(360deg)}}.animate-spin-slow{animation:spin-slow 4s linear infinite}`}</style>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="my-strategy">
        {/* Hero + context (compact) */}
        <div className="relative overflow-hidden rounded-2xl p-5 border" style={{ background: `linear-gradient(120deg, ${C.surface}, ${C.bg})`, borderColor: C.cyan, boxShadow: `0 0 30px ${C.cyan}33` }}>
          <div className="absolute -top-20 -right-16 w-80 h-80 rounded-full blur-3xl opacity-30" style={{ background: `radial-gradient(circle, ${C.cyan}, transparent 65%)` }} />
          <div className="relative flex flex-wrap items-center justify-between gap-5">
            <div className="max-w-xl">
              <div className="flex items-baseline gap-3 flex-wrap">
                <h1 className="font-outfit text-3xl font-bold" style={{ color: C.text }}>My Strategy</h1>
                <span className="font-outfit italic text-sm" style={{ color: C.cyan }}>Plan today. A life you'll love tomorrow.</span>
              </div>
              <p className="text-sm mt-1.5 leading-snug" style={{ color: C.text2 }}>{v.intro}</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="rounded-xl p-3 border grid grid-cols-2 gap-x-5 gap-y-2" style={box(C.blue)} data-testid="context-card">
                <div className="flex items-center gap-2"><Briefcase className="w-4 h-4" style={{ color: C.cyan }} /><div><p className="text-[9px] uppercase" style={{ color: C.muted }}>Career Target</p><p className="text-[13px] font-semibold" style={{ color: C.text }}>{h.career_target}</p></div></div>
                <div className="flex items-center gap-2"><MapPin className="w-4 h-4" style={{ color: C.cyan }} /><div><p className="text-[9px] uppercase" style={{ color: C.muted }}>Location</p><p className="text-[13px] font-semibold" style={{ color: C.text }}>{h.location}</p></div></div>
                <div className="flex items-center gap-2"><Database className="w-4 h-4" style={{ color: C.blue }} /><div><p className="text-[9px] uppercase" style={{ color: C.muted }}>Lifestyle Need</p><p className="text-[13px] font-semibold" style={{ color: C.text }}>{money(h.lifestyle_need)}</p></div></div>
                <div className="flex items-center gap-2"><BarChart3 className="w-4 h-4" style={{ color: C.blue }} /><div><p className="text-[9px] uppercase flex items-center gap-1" style={{ color: C.muted }}>Median <Info className="w-2.5 h-2.5" title="BLS OEWS regional" /></p><p className="text-[13px] font-semibold" style={{ color: C.text }}>{money(h.median_salary)}</p></div></div>
              </div>
              <div className="rounded-xl p-3 text-center border w-[150px]" style={box(h.on_track ? C.teal : C.gold, true)} data-testid="on-track-verdict">
                <CheckCircle2 className="w-6 h-6 mx-auto" style={{ color: h.on_track ? C.teal : C.gold }} />
                <p className="text-sm font-bold mt-1" style={{ color: h.on_track ? C.teal : C.gold }}>{h.on_track ? 'On Track' : 'Adjust'}</p>
                <p className="text-[10px] mt-0.5" style={{ color: C.text2 }}>{h.on_track ? 'Supports your target lifestyle.' : 'May need adjustment.'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* My Game Plan */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-outfit text-3xl font-bold" style={{ color: C.text }}>My Game Plan</h2>
            <p className="text-sm mt-1" style={{ color: C.text2 }}>A dynamic roadmap based on your goals, academic profile, and real-world opportunities.</p>
          </div>
          <div className="flex items-center gap-1 rounded-full p-1 border" style={{ background: C.surface, borderColor: C.border }}>
            {TABS.map((t) => (
              <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t.toLowerCase()}`}
                className="px-3 py-1.5 rounded-full text-[13px] font-semibold transition-colors"
                style={{ background: tab === t ? `linear-gradient(90deg,${C.cyan},${C.electric})` : 'transparent', color: tab === t ? '#021735' : C.text2 }}>{t}</button>
            ))}
          </div>
        </div>

        {/* Stage rail */}
        <div className="flex flex-wrap gap-3 md:gap-4">
          {v.stages.map((s, i) => <StageNode key={s.key} stage={s} idx={i} total={v.stages.length} />)}
        </div>

        {/* Tab content */}
        {tab === 'Network' ? (
          <div className="rounded-2xl p-6 border" style={box(C.cyan, true)} data-testid="tab-network-panel">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Users className="w-5 h-5" style={{ color: C.cyan }} /><h3 className="font-outfit text-xl font-semibold" style={{ color: C.text }}>Network Strategy</h3></div>
              <span className="text-[11px] px-2.5 py-0.5 rounded-full font-semibold" style={{ background: `${C.gold}22`, color: C.gold }}>{v.network.status}</span>
            </div>
            <p className="text-sm mt-2" style={{ color: C.text2 }}>{v.network.focus}</p>
            <p className="text-[11px] uppercase mt-4 mb-2" style={{ color: C.muted }}>This term's targets</p>
            <div className="grid sm:grid-cols-3 gap-3">
              {v.network.items.map((it, i) => (
                <div key={i} className="rounded-xl p-4 border flex items-start gap-2" style={box(C.cyan)}><CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: C.teal }} /><span className="text-[13px]" style={{ color: C.text }}>{it}</span></div>
              ))}
            </div>
          </div>
        ) : tab === 'Brand' ? (
          <div className="rounded-2xl p-6 border" style={box(C.purple, true)} data-testid="tab-brand-panel">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><User className="w-5 h-5" style={{ color: C.purple }} /><h3 className="font-outfit text-xl font-semibold" style={{ color: C.text }}>Personal Brand Strategy</h3></div>
              <span className="text-[11px] px-2.5 py-0.5 rounded-full font-semibold" style={{ background: `${C.gold}22`, color: C.gold }}>{v.brand.status}</span>
            </div>
            <p className="text-sm mt-2" style={{ color: C.text2 }}>{v.brand.focus}</p>
            <p className="text-[11px] uppercase mt-4 mb-2" style={{ color: C.muted }}>Current focus</p>
            <div className="grid sm:grid-cols-3 gap-3">
              {v.brand.items.map((it, i) => (
                <div key={i} className="rounded-xl p-4 border flex items-start gap-2" style={box(C.purple)}><CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: C.teal }} /><span className="text-[13px]" style={{ color: C.text }}>{it}</span></div>
              ))}
            </div>
            <Link to="/app/portfolio" className="mt-4 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-bold" style={{ color: '#021735', background: `linear-gradient(90deg,${C.purple},${C.electric})` }}>Build your brand <ArrowRight className="w-4 h-4" /></Link>
          </div>
        ) : shown.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="milestone-cards">
            {shown.map((m) => {
              const st = STATUS[m.status] || STATUS.not_started;
              const current = m.status === 'in_progress';
              return (
                <div key={m.n} className="relative rounded-2xl p-4 border flex flex-col" data-testid={`milestone-${m.n}`} style={box(current ? C.cyan : C.blue, current)}>
                  <div className="flex items-center justify-between">
                    <span className="w-6 h-6 rounded-full flex items-center justify-center text-[12px] font-bold fp-num" style={{ background: `${C.cyan}22`, color: C.cyan }}>{m.n}</span>
                    <FileText className="w-4 h-4" style={{ color: C.muted }} />
                  </div>
                  <p className="text-[11px] mt-2" style={{ color: C.muted }}>{m.phase}</p>
                  <p className="text-sm font-semibold leading-snug flex-1" style={{ color: C.text }}>{m.title}</p>
                  <span className="mt-2 inline-flex w-fit text-[11px] px-2 py-0.5 rounded-full font-semibold" style={{ background: `${st.hex}22`, color: st.hex }}>{done[m.n] ? 'Completed' : st.label}</span>
                  <p className="text-[11px] mt-2" style={{ color: C.muted }}>Due {m.date}</p>
                  <Link to={SOURCE_ROUTE[m.source] || '/app/career-intelligence'} className="text-[10px] mt-1 inline-flex items-center gap-1" style={{ color: C.cyan }}>From {m.source} <ArrowRight className="w-2.5 h-2.5" /></Link>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="rounded-2xl p-6 border flex flex-wrap items-center justify-between gap-4" style={box(C.blue)} data-testid="tab-empty">
            <div>
              <p className="text-sm font-semibold" style={{ color: C.text }}>No {tab.toLowerCase()} milestones are scheduled yet</p>
              <p className="text-[13px] mt-1" style={{ color: C.text2 }}>
                {tab === 'Courses' ? 'Course milestones appear here once your academic plan is set.' : tab === 'Skills' ? 'Your skill-building milestones will show here as gaps are identified.' : 'Milestones for this dimension will appear as your strategy calls for them.'}
              </p>
            </div>
            <Link to={tab === 'Skills' ? '/app/skills' : tab === 'Experience' ? '/app/internships' : '/app/career-intelligence'} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-bold" style={{ color: '#021735', background: `linear-gradient(90deg,${C.cyan},${C.electric})` }}>Explore {tab} <ArrowRight className="w-4 h-4" /></Link>
          </div>
        )}

        {/* Current Priority */}
        {v.current_priority && (
          <div className="rounded-2xl p-6 border" style={box(C.cyan, true)} data-testid="current-priority">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <FileText className="w-6 h-6 mt-1" style={{ color: C.cyan }} />
                <div>
                  <p className="text-[11px] uppercase tracking-wide" style={{ color: C.cyan }}>Current Priority</p>
                  <h3 className="font-outfit text-2xl font-semibold" style={{ color: C.text }}>{v.current_priority.title}</h3>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold" style={{ background: `${C.gold}22`, color: C.gold }}>{done[v.current_priority.n] ? 'Completed' : STATUS[v.current_priority.status]?.label}</span>
                    <span className="text-[12px]" style={{ color: C.muted }}>Due {v.current_priority.date}</span>
                  </div>
                </div>
              </div>
              <button onClick={() => setPriorityOpen((o) => !o)} data-testid="priority-toggle" style={{ color: C.muted }}><ChevronDown className="w-5 h-5 transition-transform" style={{ transform: priorityOpen ? 'rotate(180deg)' : 'none' }} /></button>
            </div>
            {priorityOpen && (
              <div className="mt-4 grid lg:grid-cols-[1.4fr_1fr] gap-5">
                <div>
                  <p className="text-[11px] uppercase mb-1" style={{ color: C.muted }}>Why this matters now</p>
                  <p className="text-sm leading-relaxed" style={{ color: C.text2 }}>{v.current_priority.reason}</p>
                  <p className="text-[11px] uppercase mt-3 mb-1" style={{ color: C.muted }}>What success looks like</p>
                  <p className="text-sm" style={{ color: C.text2 }}>{v.current_priority.success}</p>
                </div>
                <div className="flex flex-col gap-2 justify-center">
                  <Link to={SOURCE_ROUTE[v.current_priority.source] || '/app/skills'} data-testid="priority-work"
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold" style={{ color: '#021735', background: `linear-gradient(90deg,${C.cyan},${C.electric})` }}>
                    Work on this <ArrowRight className="w-4 h-4" />
                  </Link>
                  <button onClick={() => setDone((d) => ({ ...d, [v.current_priority.n]: !d[v.current_priority.n] }))} data-testid="priority-complete"
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border" style={{ color: C.text, borderColor: C.border }}>
                    <CheckCircle2 className="w-4 h-4" style={{ color: done[v.current_priority.n] ? C.teal : C.muted }} /> {done[v.current_priority.n] ? 'Completed' : 'Mark Complete'}
                  </button>
                  <p className="text-[11px] text-center" style={{ color: C.muted }}>Source: {v.current_priority.sources?.join(', ')}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Financial trajectory (moved up) */}
        {v.financial && (
          <div className="rounded-2xl p-6 border flex flex-wrap items-center justify-between gap-6" style={box(C.teal)} data-testid="financial-trajectory">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-6 h-6" style={{ color: C.teal }} />
              <div><h3 className="font-outfit text-xl font-semibold" style={{ color: C.text }}>Financial Trajectory</h3><p className="text-[12px] max-w-xs" style={{ color: C.text2 }}>Potential earnings path for {h.career_target} in {h.location}.</p></div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {[['Launch', v.financial.launch, C.blue], ['Year 3', v.financial.yr3, C.cyan], ['Year 5', v.financial.yr5, C.teal], ['10-Year Earnings', v.financial.tenyear, C.gold]].map((f, i, arr) => (
                <div key={f[0]} className="flex items-center gap-3">
                  <div className="text-center"><p className="font-outfit text-2xl font-bold fp-num" style={{ color: f[2], textShadow: `0 0 14px ${f[2]}66` }}>{moneyK(f[1])}</p><p className="text-[11px]" style={{ color: C.muted }}>{f[0]}</p></div>
                  {i < arr.length - 1 && <ArrowRight className="w-4 h-4" style={{ color: C.muted }} />}
                </div>
              ))}
            </div>
            <Link to="/app/college-financial" data-testid="view-financial-intel" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-bold" style={{ color: C.text, background: `linear-gradient(90deg,${C.electric},${C.purple})`, boxShadow: `0 0 18px ${C.purple}66` }}>
              View Financial Intelligence <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="text-[11px] w-full" style={{ color: C.muted }} title={v.financial.source}>{v.financial.note}</p>
          </div>
        )}

        {/* Network + Brand + Health */}
        <div className="grid lg:grid-cols-3 gap-5">
          <div className="rounded-2xl p-5 border" style={box(C.cyan)} data-testid="network-strategy">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Users className="w-5 h-5" style={{ color: C.cyan }} /><h3 className="font-outfit text-lg font-semibold" style={{ color: C.text }}>Network Strategy</h3></div>
              <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background: `${C.gold}22`, color: C.gold }}>{v.network.status}</span>
            </div>
            <p className="text-[13px] mt-2" style={{ color: C.text2 }}>{v.network.focus}</p>
            <ul className="mt-3 space-y-1.5">{v.network.items.map((it, i) => <li key={i} className="flex gap-2 text-[13px]" style={{ color: C.text2 }}><CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: C.teal }} />{it}</li>)}</ul>
            <button onClick={() => setTab('Network')} className="text-[12px] mt-3 inline-flex items-center gap-1" style={{ color: C.cyan }}>See full network plan <ArrowRight className="w-3 h-3" /></button>
          </div>
          <div className="rounded-2xl p-5 border" style={box(C.purple)} data-testid="brand-strategy">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><User className="w-5 h-5" style={{ color: C.purple }} /><h3 className="font-outfit text-lg font-semibold" style={{ color: C.text }}>Personal Brand</h3></div>
              <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background: `${C.gold}22`, color: C.gold }}>{v.brand.status}</span>
            </div>
            <p className="text-[13px] mt-2" style={{ color: C.text2 }}>{v.brand.focus}</p>
            <ul className="mt-3 space-y-1.5">{v.brand.items.map((it, i) => <li key={i} className="flex gap-2 text-[13px]" style={{ color: C.text2 }}><CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: C.teal }} />{it}</li>)}</ul>
            <button onClick={() => setTab('Brand')} className="text-[12px] mt-3 inline-flex items-center gap-1" style={{ color: C.cyan }}>See full brand plan <ArrowRight className="w-3 h-3" /></button>
          </div>
          <div className="rounded-2xl p-5 border" style={box(C.blue)} data-testid="strategy-health">
            <div className="flex items-center gap-2"><Target className="w-5 h-5" style={{ color: C.cyan }} /><h3 className="font-outfit text-lg font-semibold" style={{ color: C.text }}>Strategy Health</h3></div>
            <div className="flex items-center gap-4 mt-3">
              <div className="relative w-[84px] h-[84px] shrink-0">
                <svg viewBox="0 0 80 80" className="w-full h-full -rotate-90">
                  <circle cx="40" cy="40" r="34" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="7" />
                  <circle cx="40" cy="40" r="34" fill="none" stroke={C.cyan} strokeWidth="7" strokeLinecap="round" strokeDasharray={2 * Math.PI * 34} strokeDashoffset={2 * Math.PI * 34 * (1 - v.health.score / 100)} style={{ filter: `drop-shadow(0 0 6px ${C.cyan})` }} />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center"><span className="text-xl font-bold fp-num" style={{ color: C.text }}>{v.health.score}</span><span className="text-[9px]" style={{ color: C.muted }}>/100</span></div>
              </div>
              <div className="flex-1 space-y-1.5">
                {v.health.dimensions.map((d) => (
                  <div key={d.label} className="flex items-center justify-between text-[13px]"><span style={{ color: C.text2 }}><span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: d.hex }} />{d.label}</span><span className="fp-num font-semibold" style={{ color: C.text }}>{d.value}%</span></div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center justify-between gap-4 pt-1">
          <div className="flex items-center gap-2 text-[12px]" style={{ color: C.muted }}>
            <span>Last updated {v.meta.last_updated}</span><span>·</span><span>Your strategy updates as your goals, progress, and opportunities evolve.</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/app/my-futurepath')} data-testid="generate-report" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border" style={{ color: C.text, borderColor: C.border }}><FileText className="w-4 h-4" style={{ color: C.cyan }} /> Generate Strategy Report</button>
            <button onClick={refresh} data-testid="refresh-strategy" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold" style={{ color: C.text, background: `linear-gradient(90deg,${C.cyan},${C.electric},${C.purple})` }}><RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh Strategy</button>
          </div>
        </div>
      </motion.div>
    </AppLayout>
  );
}
